import React from 'react';
import { sha256 } from 'js-sha256';
import { ClompApi } from '../api/ClompApi';

const api = new ClompApi();

const PASSWORD_MIN_LENGTH = 8;

// The settings wheel on your own profile
export function SettingsIcon() {
    return (
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path fill="currentColor" d="M19.14 12.94c.04-.3.06-.61.06-.94s-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96a7 7 0 0 0-1.62-.94l-.36-2.54a.48.48 0 0 0-.48-.41h-3.84a.48.48 0 0 0-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.48.48 0 0 0-.59.22L2.74 8.87a.48.48 0 0 0 .12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.48.48 0 0 0-.12-.61l-2.01-1.58zM12 15.6a3.6 3.6 0 1 1 0-7.2 3.6 3.6 0 0 1 0 7.2z" />
        </svg>
    );
}

// The account settings form, shown over the profile: username, email, bio and password.
//   onClose() - the form was closed without saving; onSaved(settings) - saved, with the account as it now is
export class ProfileSettings extends React.Component {

    state = {
        loading: true,
        // the account as it was when the form opened: { username, email, bio, hasPassword, ...limits }
        saved: null,
        username: '',
        email: '',
        bio: '',
        newPassword: '',
        confirmPassword: '',
        currentPassword: '',
        saving: false,
        // { field, message }: field names the input the message belongs to, or is null for the whole form
        error: null
    };

    componentDidMount() {
        window.addEventListener('keydown', this.onKeyDown);
        api.profile.getSettings().then((saved) => {
            this.setState({ loading: false, saved, username: saved.username, email: saved.email, bio: saved.bio });
        }).catch((err) => {
            console.log('Error loading settings: ', err);
            this.setState({ loading: false, error: { field: null, message: "Couldn't load your settings. Please try again." } });
        });
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.onKeyDown);
    }

    onKeyDown = (e) => {
        if (e.key === 'Escape' && !this.state.saving) {
            this.props.onClose();
        }
    }

    // Changing the email or password of an account that has a password takes the current one
    needsCurrentPassword() {
        let { saved, email, newPassword } = this.state;
        return Boolean(saved && saved.hasPassword && (email.trim() !== saved.email || newPassword !== ''));
    }

    save = (e) => {
        e.preventDefault();
        let { saved, username, email, bio, newPassword, confirmPassword, currentPassword } = this.state;
        if (newPassword !== '') {
            if (newPassword.length < PASSWORD_MIN_LENGTH) {
                this.setState({ error: { field: 'newPassword', message: `Your new password must be at least ${PASSWORD_MIN_LENGTH} characters` } });
                return;
            }
            if (newPassword !== confirmPassword) {
                this.setState({ error: { field: 'confirmPassword', message: "The new passwords don't match" } });
                return;
            }
        }
        if (this.needsCurrentPassword() && currentPassword === '') {
            this.setState({ error: { field: 'currentPassword', message: 'Enter your current password to change your email or password' } });
            return;
        }

        // only what has changed is sent; passwords go hashed, as they do when logging in
        let changes = {};
        if (username.trim() !== saved.username) {
            changes.username = username.trim();
        }
        if (email.trim() !== saved.email) {
            changes.email = email.trim();
        }
        if (bio !== saved.bio) {
            changes.bio = bio;
        }
        if (newPassword !== '') {
            changes.newPassword = sha256(newPassword);
        }
        if (this.needsCurrentPassword()) {
            changes.currentPassword = sha256(currentPassword);
        }
        if (Object.keys(changes).length === 0) {
            this.props.onClose();
            return;
        }

        this.setState({ saving: true, error: null });
        api.profile.saveSettings(changes).then((settings) => {
            this.props.onSaved(settings);
        }).catch((err) => {
            let data = err.response && err.response.data;
            if (err.response && err.response.status === 401) {
                // the login ran out while the form was open
                this.props.onClose();
                return;
            }
            this.setState({ saving: false, error: { field: (data && data.field) || null, message: (data && data.message) || "Couldn't save your settings. Please try again." } });
        });
    }

    // A labelled input, with the form's error under it when the error is about this field
    field(name, label, props, hint) {
        let error = this.state.error && this.state.error.field === name ? this.state.error.message : null;
        return (
            <div className="pf-field">
                <label htmlFor={`pf-settings-${name}`}>{label}</label>
                <input id={`pf-settings-${name}`} name={name} value={this.state[name]} aria-invalid={Boolean(error)}
                    aria-describedby={error ? `pf-settings-${name}-error` : undefined}
                    onChange={(e) => this.setState({ [name]: e.target.value })} {...props} />
                {error ? <p className="pf-error" id={`pf-settings-${name}-error`} role="alert">{error}</p> : hint ? <p className="pf-field-hint">{hint}</p> : null}
            </div>
        );
    }

    renderForm() {
        let { saved, bio, saving, error } = this.state;
        let bioError = error && error.field === 'bio' ? error.message : null;
        return (
            <form onSubmit={this.save} noValidate>
                {this.field('username', 'Username', { type: 'text', autoComplete: 'username', maxLength: saved.usernameMaxLength, autoFocus: true },
                    `${saved.usernameMinLength} to ${saved.usernameMaxLength} letters, numbers and underscores. Your profile's address changes with it.`)}
                {this.field('email', 'Email', { type: 'email', autoComplete: 'email', maxLength: 255 })}
                <div className="pf-field">
                    <label htmlFor="pf-settings-bio">Biography</label>
                    <textarea id="pf-settings-bio" className="pf-bio-input" rows="4" maxLength={saved.bioMaxLength} value={bio}
                        placeholder="Tell other players a bit about yourself." onChange={(e) => this.setState({ bio: e.target.value })} />
                    {bioError ? <p className="pf-error" role="alert">{bioError}</p> : <p className="pf-field-hint">{bio.length} / {saved.bioMaxLength}</p>}
                </div>

                <h3 className="pf-settings-sub">{saved.hasPassword ? 'Change password' : 'Set a password'}</h3>
                {saved.hasPassword ? null : <p className="pf-field-hint pf-settings-note">You sign in with Google. Setting a password lets you log in with your username as well.</p>}
                {this.field('newPassword', 'New password', { type: 'password', autoComplete: 'new-password' }, saved.hasPassword ? 'Leave empty to keep your current password.' : null)}
                {this.state.newPassword !== '' ? this.field('confirmPassword', 'Confirm new password', { type: 'password', autoComplete: 'new-password' }) : null}
                {this.needsCurrentPassword() ?
                    this.field('currentPassword', 'Current password', { type: 'password', autoComplete: 'current-password' }, 'Needed to change your email or password.')
                    : null}

                {error && error.field === null ? <p className="pf-error" role="alert">{error.message}</p> : null}
                <div className="pf-settings-actions">
                    <button type="button" className="pf-btn pf-btn-ghost" disabled={saving} onClick={this.props.onClose}>Cancel</button>
                    <button type="submit" className="pf-btn pf-btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</button>
                </div>
            </form>
        );
    }

    render() {
        let { loading, saved, error } = this.state;
        return (
            <div className="pf-modal" role="dialog" aria-modal="true" aria-labelledby="pf-settings-title" onMouseDown={(e) => e.target === e.currentTarget && !this.state.saving && this.props.onClose()}>
                <div className="pf-modal-body">
                    <h2 id="pf-settings-title">Account settings</h2>
                    {loading ? <p className="pf-empty">Loading your settings...</p>
                        : saved ? this.renderForm()
                        :
                        <>
                            <p className="pf-error" role="alert">{error ? error.message : null}</p>
                            <div className="pf-settings-actions">
                                <button type="button" className="pf-btn pf-btn-ghost" onClick={this.props.onClose}>Close</button>
                            </div>
                        </>
                    }
                </div>
            </div>
        );
    }

}
